export const round2 = (n) => (Number.isFinite(n) ? Math.round(n * 100) / 100 : n);
export const toNum = (v) => (v === "" || v == null ? null : Number(v));

export const calcTargetPriceFromPercentage = (finalPrice, percentage) => {
  const fp = toNum(finalPrice);
  const p = toNum(percentage);
  if (fp == null || p == null) return null;
  return round2(fp * (1 + p / 100));
};

export const calcPercentageFromTargetPrice = (finalPrice, targetPrice) => {
  const fp = toNum(finalPrice);
  const tp = toNum(targetPrice);
  if (fp == null || tp == null || fp === 0) return null;
  return round2(((tp - fp) / fp) * 100);
};

// single-variant sync
// baseFinalPrice: the base finalPrice to use for percentage calculations (latest saved finalPrice)
// originalFinalPrice: optional parameter for fallback when baseFinalPrice is not available (for unsaved changes)
// pricingMode: "dropshipping" or "wholesale" - determines which pricing fields to update
export const syncVariantPricing = (variant, changedField, baseFinalPrice = null, originalFinalPrice = null, pricingMode = "dropshipping") => {
  const v = { ...(variant || {}) };
  
  // Handle Dropshipping mode
  if (pricingMode === "dropshipping") {
    const fp = toNum(v.finalPrice);

    // Auto-calc variant MRP from finalPrice (finalPriceMRP = finalPrice * 2.5)
    if (fp != null) {
      v.finalPriceMRP = round2(fp * 2.5);
    } else {
      v.finalPriceMRP = v.finalPriceMRP ?? null;
    }

    // Get the base finalPrice to use for calculations
    // Priority: baseFinalPrice (latest saved) > originalFinalPrice (fallback) > current finalPrice
    let baseFp = baseFinalPrice != null ? toNum(baseFinalPrice) : (originalFinalPrice != null ? toNum(originalFinalPrice) : toNum(variant.finalPrice));

  if (changedField === "percentage") {
    // When percentage changes, calculate targetPrice from LATEST finalPrice (baseFp)
    const p = toNum(v.percentage);
    
    // If percentage is 0 or empty, reset finalPrice and targetPrice to base finalPrice
    if (p === 0 || p === null || v.percentage === "" || v.percentage == null) {
      // Reset to base finalPrice (original value)
      if (baseFp != null) {
        v.finalPrice = baseFp;
        v.targetPrice = baseFp;
        v.finalPriceMRP = round2(baseFp * 2.5);
      } else if (fp != null) {
        // Fallback: if baseFp not available, keep current but update MRP
        v.finalPriceMRP = round2(fp * 2.5);
      }
    } else if (baseFp != null) {
      // Calculate from latest finalPrice (baseFp)
      const tp = calcTargetPriceFromPercentage(baseFp, p);
      if (tp != null) {
        v.targetPrice = tp;
        // Set finalPrice = targetPrice
        v.finalPrice = tp;
        // Recalculate MRP based on new finalPrice
        v.finalPriceMRP = round2(tp * 2.5);
      }
    }
  }

  if (changedField === "targetPrice") {
    // When targetPrice changes, set finalPrice = targetPrice and calculate percentage
    const tp = toNum(v.targetPrice);
    // Handle targetPrice = 0 or empty - reset to base price
    if (tp === 0 || tp === null || v.targetPrice === "" || v.targetPrice == null) {
      // Reset to base finalPrice (original value)
      if (baseFp != null) {
        v.finalPrice = baseFp;
        v.targetPrice = baseFp;
        v.finalPriceMRP = round2(baseFp * 2.5);
        v.percentage = 0;
      }
    } else if (tp != null) {
      // Set finalPrice = targetPrice
      v.finalPrice = tp;
      // Recalculate MRP based on new finalPrice
      v.finalPriceMRP = round2(tp * 2.5);
      
      // Calculate percentage from LATEST finalPrice (baseFp - latest saved finalPrice)
      // Use baseFp (latest saved finalPrice) as base for percentage calculation
      if (baseFp != null && baseFp !== 0) {
        const p = calcPercentageFromTargetPrice(baseFp, tp);
        if (p != null) v.percentage = p;
      }
    }
  }

    if (changedField === "finalPrice") {
      // finalPrice is now read-only, but keep this for backward compatibility
      const newFp = toNum(v.finalPrice);
      if (newFp != null) {
        v.finalPriceMRP = round2(newFp * 2.5);
        if (v.percentage !== "" && v.percentage != null) {
          const tp = calcTargetPriceFromPercentage(newFp, v.percentage);
          if (tp != null) v.targetPrice = tp;
        } else if (v.targetPrice !== "" && v.targetPrice != null) {
          const p = calcPercentageFromTargetPrice(newFp, v.targetPrice);
          if (p != null) v.percentage = p;
        }
      }
    }
  }
  
  // Handle Wholesale mode
  if (pricingMode === "wholesale") {
    const wfp = toNum(v.wholesaleFinalPrice);

    // Don't calculate MRP for wholesale (as per user requirement)

    // Get the base wholesaleFinalPrice to use for calculations
    // CRITICAL: Only use baseFinalPrice (immutable original saved price) or originalFinalPrice (fallback)
    // NEVER use variant.wholesaleFinalPrice as it may be already modified, causing cumulative calculations
    let baseWfp = baseFinalPrice != null ? toNum(baseFinalPrice) : (originalFinalPrice != null ? toNum(originalFinalPrice) : null);

    if (changedField === "wholesalePercentage") {
      const p = toNum(v.wholesalePercentage);
      
      if (p === 0 || p === null || v.wholesalePercentage === "" || v.wholesalePercentage == null) {
        if (baseWfp != null) {
          v.wholesaleFinalPrice = baseWfp;
          v.wholesaleTargetPrice = baseWfp;
        }
      } else if (baseWfp != null) {
        const tp = calcTargetPriceFromPercentage(baseWfp, p);
        if (tp != null) {
          v.wholesaleTargetPrice = tp;
          v.wholesaleFinalPrice = tp;
        }
      }
    }

    if (changedField === "wholesaleTargetPrice") {
      const tp = toNum(v.wholesaleTargetPrice);
      // Handle targetPrice = 0 or empty - reset to base price
      if (tp === 0 || tp === null || v.wholesaleTargetPrice === "" || v.wholesaleTargetPrice == null) {
        // Reset to base wholesaleFinalPrice (original value)
        if (baseWfp != null) {
          v.wholesaleFinalPrice = baseWfp;
          v.wholesaleTargetPrice = baseWfp;
          v.wholesalePercentage = 0;
        }
      } else if (tp != null) {
        v.wholesaleFinalPrice = tp;
        
        if (baseWfp != null && baseWfp !== 0) {
          const p = calcPercentageFromTargetPrice(baseWfp, tp);
          if (p != null) v.wholesalePercentage = p;
        }
      }
    }

    if (changedField === "wholesaleFinalPrice") {
      const newWfp = toNum(v.wholesaleFinalPrice);
      if (newWfp != null) {
        if (v.wholesalePercentage !== "" && v.wholesalePercentage != null) {
          const tp = calcTargetPriceFromPercentage(newWfp, v.wholesalePercentage);
          if (tp != null) v.wholesaleTargetPrice = tp;
        } else if (v.wholesaleTargetPrice !== "" && v.wholesaleTargetPrice != null) {
          const p = calcPercentageFromTargetPrice(newWfp, v.wholesaleTargetPrice);
          if (p != null) v.wholesalePercentage = p;
        }
      }
    }
  }

  return v;
};

// bulk apply (works for both draft + product list)
// selectionMap optional: if passed, apply only where selectionMap[key] is true
// originalVariations: optional array of original variations to get base finalPrice from
// firstTimePriceMap: optional map of latest/base finalPrice (key -> basePrice) - can be latest saved or first-time price
// pricingMode: "dropshipping" or "wholesale" - determines which pricing fields to update
export const applyBulkPricing = ({
  variations = [],
  selectionMap,
  getKey,
  mode, // "percentage" | "targetPrice"
  value,
  originalVariations = null, // optional: original variations to get base finalPrice
  firstTimePriceMap = null, // optional: map of latest/base finalPrice (latest saved or first-time)
  pricingMode = "dropshipping", // "dropshipping" | "wholesale"
}) => {
  const val = value === "" ? "" : Number(value);

  return (variations || []).map((v, i) => {
    const key = getKey ? getKey(v, i) : String(i);
    if (selectionMap && key && !selectionMap[key]) return v;

    const next = { ...v };
    
    // Get base finalPrice for calculations (latest saved or first-time)
    // Priority: firstTimePriceMap (latest/base) > originalVariations > current finalPrice
    let baseFp = toNum(next.finalPrice);
    
    // Priority 1: Use latest/base price from map if available
    if (firstTimePriceMap && typeof firstTimePriceMap === 'object' && firstTimePriceMap[key] != null) {
      baseFp = toNum(firstTimePriceMap[key]);
    }
    // Priority 2: Use originalVariations if firstTimePriceMap doesn't have it
    else if (originalVariations && Array.isArray(originalVariations)) {
      // First try to get by index (most common case - same order)
      let origMatch = originalVariations[i];
      
      // If index-based doesn't have finalPrice or doesn't match by identifier, try to find by skuId/specId
      if (!origMatch || (origMatch.finalPrice == null && (v.skuId || v.specId))) {
        origMatch = originalVariations.find(ov => 
          (ov.skuId && v.skuId && String(ov.skuId) === String(v.skuId)) ||
          (ov.specId && v.specId && String(ov.specId) === String(v.specId))
        ) || origMatch;
      }
      
      if (origMatch) {
        const origFp = toNum(origMatch.finalPrice);
        if (origFp != null) {
          baseFp = origFp;
        }
      }
    }

    if (pricingMode === "dropshipping") {
      if (mode === "percentage") {
        next.percentage = val;
        // Calculate targetPrice from LATEST finalPrice (baseFp)
        // Handle percentage = 0 case
        if (val === 0 || val === null || val === "") {
          // Reset to base finalPrice (original value)
          if (baseFp != null) {
            next.finalPrice = baseFp;
            next.targetPrice = baseFp;
            next.finalPriceMRP = round2(baseFp * 2.5);
          } else if (next.finalPrice != null) {
            // Fallback: if baseFp not available, keep current but update MRP
            next.finalPriceMRP = round2(next.finalPrice * 2.5);
          }
        } else if (baseFp != null) {
          const tp = calcTargetPriceFromPercentage(baseFp, val);
          if (tp != null) {
            next.targetPrice = tp;
            // Set finalPrice = targetPrice
            next.finalPrice = tp;
            // Recalculate MRP based on new finalPrice
            next.finalPriceMRP = round2(tp * 2.5);
          }
        }
        return next;
      }

      if (mode === "targetPrice") {
        // Handle targetPrice = 0 or empty - reset to base price
        if (val === 0 || val === null || val === "") {
          if (baseFp != null) {
            next.finalPrice = baseFp;
            next.targetPrice = baseFp;
            next.finalPriceMRP = round2(baseFp * 2.5);
            next.percentage = 0;
          }
        } else {
          next.targetPrice = val;
          // Set finalPrice = targetPrice
          next.finalPrice = val;
          // Recalculate MRP based on new finalPrice
          next.finalPriceMRP = round2(val * 2.5);
          
          // Calculate percentage from LATEST finalPrice (baseFp)
          if (baseFp != null && baseFp !== 0) {
            const p = calcPercentageFromTargetPrice(baseFp, val);
            if (p != null) next.percentage = p;
          }
        }
        return next;
      }
    }
    
    // Wholesale mode
    if (pricingMode === "wholesale") {
      // Get base wholesaleFinalPrice for calculations
      // CRITICAL: Only use firstTimePriceMap (immutable original saved price) or originalVariations
      // NEVER use next.wholesaleFinalPrice as it may be already modified, causing cumulative calculations
      let baseWfp = null;
      if (firstTimePriceMap && typeof firstTimePriceMap === 'object' && firstTimePriceMap[key] != null) {
        baseWfp = toNum(firstTimePriceMap[key]);
      } else if (originalVariations && Array.isArray(originalVariations)) {
        let origMatch = originalVariations[i];
        if (!origMatch || (origMatch.wholesaleFinalPrice == null && (next.skuId || next.specId))) {
          origMatch = originalVariations.find(ov => 
            (ov.skuId && next.skuId && String(ov.skuId) === String(next.skuId)) ||
            (ov.specId && next.specId && String(ov.specId) === String(next.specId))
          ) || origMatch;
        }
        if (origMatch) {
          const origWfp = toNum(origMatch.wholesaleFinalPrice);
          if (origWfp != null) {
            baseWfp = origWfp;
          }
        }
      }
      
      if (mode === "percentage") {
        next.wholesalePercentage = val;
        if (val === 0 || val === null || val === "") {
          if (baseWfp != null) {
            next.wholesaleFinalPrice = baseWfp;
            next.wholesaleTargetPrice = baseWfp;
          }
        } else if (baseWfp != null) {
          const tp = calcTargetPriceFromPercentage(baseWfp, val);
          if (tp != null) {
            next.wholesaleTargetPrice = tp;
            next.wholesaleFinalPrice = tp;
          }
        }
        return next;
      }

      if (mode === "targetPrice") {
        // Handle targetPrice = 0 or empty - reset to base price
        if (val === 0 || val === null || val === "") {
          if (baseWfp != null) {
            next.wholesaleFinalPrice = baseWfp;
            next.wholesaleTargetPrice = baseWfp;
            next.wholesalePercentage = 0;
          }
        } else {
          next.wholesaleTargetPrice = val;
          next.wholesaleFinalPrice = val;
          
          if (baseWfp != null && baseWfp !== 0) {
            const p = calcPercentageFromTargetPrice(baseWfp, val);
            if (p != null) next.wholesalePercentage = p;
          }
        }
        return next;
      }
    }

    return next;
  });
};
